import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiRequest } from '@/lib/queryClient';
import { Users, BookOpen, DollarSign, TrendingUp, Activity, Settings, AlertCircle, Calendar, FileText, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminNavigation from '@/components/AdminNavigation';
import AdminFooter from '@/components/AdminFooter';

interface DashboardStats {
  totalUsers: number;
  activeSubscriptions: number;
  totalStories: number;
  revenue: {
    monthly: number;
    total: number;
  };
  userGrowth: {
    daily: number;
    weekly: number;
    monthly: number;
  };
}

interface UserData {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  createdAt: string;
  subscriptionStatus: string;
  trialEndsAt?: string;
  storiesCount?: number;
}

export default function Admin() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [timeRange, setTimeRange] = useState('7d');

  // Check if user is admin
  const isAdmin = user?.email?.includes('@VibeCaaS.com') || user?.email?.includes('@neuralquantum.ai');

  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['/api/admin/stats', timeRange],
    enabled: isAdmin,
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/stats?range=${timeRange}`);
      return response.json() as Promise<DashboardStats>;
    }
  });

  // Fetch recent users
  const { data: recentUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['/api/admin/users'],
    enabled: isAdmin,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/users?limit=10');
      return response.json() as Promise<{ users: UserData[] }>;
    }
  });

  // Fetch system health
  const { data: health } = useQuery({
    queryKey: ['/api/admin/health'],
    enabled: isAdmin,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/health');
      return response.json();
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-teal-50 to-amber-50 p-4 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">You don't have permission to access the admin dashboard.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-overview">
      <AdminNavigation currentPage="Admin Overview" />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">Monitor and manage your SnuggleCrafters.com platform</p>
        </div>

        {/* Time Range Selector */}
        <div className="flex justify-end">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-full md:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1d">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {statsLoading ? '...' : stats?.totalUsers || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                +{stats?.userGrowth?.daily || 0} today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Active Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-teal-600">
                {statsLoading ? '...' : stats?.activeSubscriptions || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ${stats?.revenue?.monthly || 0}/mo
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Total Stories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {statsLoading ? '...' : stats?.totalStories || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Generated stories
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                ${statsLoading ? '...' : stats?.revenue?.total || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Lifetime earnings
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="stories">Stories</TabsTrigger>
            <TabsTrigger value="health">System Health</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Recent Users</CardTitle>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading users...</div>
                ) : (
                  <div className="space-y-4">
                    {recentUsers?.users?.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <div className="font-medium">{user.email}</div>
                          <div className="text-sm text-gray-500">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Joined {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={user.subscriptionStatus === 'active' ? 'default' : 'secondary'}>
                            {user.subscriptionStatus}
                          </Badge>
                          {user.storiesCount !== undefined && (
                            <div className="text-sm text-gray-500 mt-1">
                              {user.storiesCount} stories
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stories Tab */}
          <TabsContent value="stories">
            <Card>
              <CardHeader>
                <CardTitle>Story Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-gray-500">
                  Story analytics coming soon...
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Health Tab */}
          <TabsContent value="health">
            <Card>
              <CardHeader>
                <CardTitle>System Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-green-600" />
                      <span className="font-medium">API Status</span>
                    </div>
                    <Badge variant="default" className="bg-green-600">
                      {health?.api?.status || 'Healthy'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Database</span>
                    </div>
                    <Badge variant="default" className="bg-green-600">
                      {health?.database?.status || 'Connected'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                      <span className="font-medium">Memory Usage</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {health?.memory?.usage || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-amber-600" />
                      <span className="font-medium">Uptime</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {health?.uptime || 'N/A'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <AdminFooter />
    </div>
  );
}